import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';
import { setTranslation, getActiveTranslationId, DEFAULT_TRANSLATION, TRANSLATIONS } from '../utils/bibleData';

const TranslationContext = createContext(null);

const LS_KEY = 'fb_bibleTranslation';

function readInitial() {
  try {
    const v = localStorage.getItem(LS_KEY);
    if (v && TRANSLATIONS.find((t) => t.id === v)) return v;
  } catch {}
  return DEFAULT_TRANSLATION;
}

export function TranslationProvider({ children }) {
  const { currentUser } = useAuth();
  const [translation, setTranslationState] = useState(readInitial);
  // `version` bumps whenever the active dataset changes so verse-text consumers
  // (which memoize on book/chapter/verse) recompute against the new translation.
  const [version, setVersion] = useState(0);
  const appliedRef = useRef(null);

  // Apply a translation to the bibleData module, then bump the render version.
  const apply = useCallback(async (id) => {
    const actual = await setTranslation(id);
    appliedRef.current = actual;
    setTranslationState(actual);
    setVersion((v) => v + 1);
    return actual;
  }, []);

  // Load the initial / locally-remembered translation on mount.
  useEffect(() => {
    if (getActiveTranslationId() !== translation || appliedRef.current === null) {
      apply(translation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reconcile with the user's saved preference once auth data is available.
  useEffect(() => {
    const pref = currentUser?.bibleTranslation;
    if (pref && TRANSLATIONS.find((t) => t.id === pref) && pref !== appliedRef.current) {
      apply(pref);
      try {
        localStorage.setItem(LS_KEY, pref);
      } catch {}
    }
  }, [currentUser?.bibleTranslation, apply]);

  // User-initiated change: switch, persist (localStorage + Firestore), re-render.
  const changeTranslation = useCallback(
    async (id) => {
      const actual = await apply(id);
      try {
        localStorage.setItem(LS_KEY, actual);
      } catch {}
      if (currentUser?.uid) {
        updateDoc(doc(db, 'users', currentUser.uid), { bibleTranslation: actual }).catch((e) =>
          console.warn('Failed to save translation preference:', e)
        );
      }
      return actual;
    },
    [apply, currentUser?.uid]
  );

  const value = {
    translation,
    version,
    changeTranslation,
    translations: TRANSLATIONS,
  };

  return <TranslationContext.Provider value={value}>{children}</TranslationContext.Provider>;
}

export function useTranslation() {
  const ctx = useContext(TranslationContext);
  if (!ctx) {
    // Safe fallback if used outside the provider.
    return {
      translation: DEFAULT_TRANSLATION,
      version: 0,
      changeTranslation: async () => {},
      translations: TRANSLATIONS,
    };
  }
  return ctx;
}
